import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { isMobileDevice } from "util/support";
import Button from "common/core/button";
import { Avatar } from "common/core/avatar";
import { Heading, Paragraph } from "common/core/typography";
import { useMobileScreenClass } from "common/core/responsive";
import { NotarizeNetwork } from "common/core/logo/notarize_network";
import { useProofRebrand } from "util/feature_detection";
import { ButtonStyledLink } from "common/core/button/button_styled_link";
import { MenuItemWrapper } from "common/core/popout_menu/common";

import Styles from "./index.module.scss";

const MESSAGES = defineMessages({
  deprecatedWidgetTitle: {
    id: "29757df8-56a1-46ac-b764-b19b685de61c",
    defaultMessage: "My account",
  },
  widgetTitle: {
    id: "9fb9fd73-f0aa-415f-8954-cc30a8574094",
    defaultMessage: "My Proof account",
  },
  signDocumentLabel: {
    id: "f750a7cb-7cff-4644-b279-1c9c90abb220",
    defaultMessage: "Sign a document",
  },
  manageAccountLabel: {
    id: "bf518b36-f486-40a6-b4a9-7883bbb093d0",
    defaultMessage: "Manage my account",
  },
  viewNotaryJournalLabel: {
    id: "a2a3ac27-1e14-4764-8699-4b17df0f2e03",
    defaultMessage: "View notary journal",
  },
  logOutLabel: {
    id: "2f495866-3f80-437e-a367-3156911238dd",
    defaultMessage: "Log out",
  },
});

type AccountInfoProps = {
  userFullName: string | undefined;
  userInitials: string | undefined;
};

type ProofIdListProps = {
  closeMenu: () => void;
  hasNotaryProfile: boolean;
  hasSignAccess: boolean;
  signDocumentsUrl: () => void;
  notaryJournalUrl: () => void;
  handleSettingsUrl: () => void;
  handleLogout: () => void;
};

export const AccountBlurb = ({ size }: { size?: "defaultSize" | "large" | "small" }) => (
  <Paragraph size={size}>
    <FormattedMessage
      id="ed4975d7-f14e-4a42-a9f2-36e3c5ef5863"
      defaultMessage="One account for everything you do with Proof.{br}Sign, verify, or connect with the {notarizeNetwork}."
      values={{ br: <br />, notarizeNetwork: <NotarizeNetwork /> }}
    />
  </Paragraph>
);

export function AccountInfo({ userInitials, userFullName }: AccountInfoProps) {
  const intl = useIntl();
  const isProof = useProofRebrand();
  const isMobileSize = useMobileScreenClass();

  return (
    <div className={Styles.accountInfo}>
      <div className={Styles.accountName}>
        <div>
          <Heading level="h2" textStyle="headingFive">
            {isProof
              ? intl.formatMessage(MESSAGES.widgetTitle)
              : intl.formatMessage(MESSAGES.deprecatedWidgetTitle)}
          </Heading>
          {userFullName && <Paragraph size="large">{userFullName}</Paragraph>}
        </div>
        <Avatar
          size={isMobileSize ? "medium" : "large"}
          userInitials={userInitials}
          kind={isProof ? "green" : "blue"}
        />
      </div>
      {isProof && <AccountBlurb size="small" />}
    </div>
  );
}

export function ProofIdList({
  closeMenu,
  hasNotaryProfile,
  hasSignAccess,
  signDocumentsUrl,
  notaryJournalUrl,
  handleSettingsUrl,
  handleLogout,
}: ProofIdListProps) {
  const intl = useIntl();

  function handleClick(onClick: () => void) {
    onClick();
    closeMenu();
  }

  return (
    <>
      {hasSignAccess && (
        <MenuItemWrapper removeInteractionState>
          <ButtonStyledLink
            buttonColor="action"
            variant="primary"
            onClick={() => handleClick(signDocumentsUrl)}
            fullwidth
            withIcon={{ name: "signature", placement: "left" }}
          >
            {intl.formatMessage(MESSAGES.signDocumentLabel)}
          </ButtonStyledLink>
        </MenuItemWrapper>
      )}

      <MenuItemWrapper removeInteractionState>
        <ButtonStyledLink
          onClick={() => handleClick(handleSettingsUrl)}
          buttonColor="action"
          variant="secondary"
          fullwidth
          withIcon={{ name: "name", placement: "left" }}
        >
          {intl.formatMessage(MESSAGES.manageAccountLabel)}
        </ButtonStyledLink>
      </MenuItemWrapper>

      {hasNotaryProfile && !isMobileDevice() && (
        <MenuItemWrapper removeInteractionState>
          <ButtonStyledLink
            onClick={() => handleClick(notaryJournalUrl)}
            buttonColor="action"
            variant="tertiary"
            fullwidth
            withIcon={{ name: "notary-journal", placement: "left" }}
          >
            {intl.formatMessage(MESSAGES.viewNotaryJournalLabel)}
          </ButtonStyledLink>
        </MenuItemWrapper>
      )}

      <MenuItemWrapper removeInteractionState>
        <Button
          automationId="log-out-button"
          onClick={handleLogout}
          variant="tertiary"
          buttonColor="dark"
          fullwidth
        >
          {intl.formatMessage(MESSAGES.logOutLabel)}
        </Button>
      </MenuItemWrapper>
    </>
  );
}
